import { VideoDimensionsI } from '@/hooks/useVideoOverlayDimensions'
import { useTranslation } from 'react-i18next'

import { Box } from '@chakra-ui/react'

import { TailgateGridQuery } from '@/graphql/generated/operations'

interface ScalableGridOverlayIProps {
  tailgateGrid: TailgateGridQuery['tailgateGrid']
  viewWidth?: number | null
  viewHeight?: number | null
  // The camera's configured resolution (the grid's coordinate space) and the
  // clip's decoded resolution. When both are known the grid is scaled from the
  // former into the latter, per-axis, matching how the evaluator scaled it.
  cameraDimensions?: VideoDimensionsI | null
  clipDimensions?: VideoDimensionsI | null
}

interface GridBoxCornerI {
  x: number
  y: number
}

interface GridBoxI {
  topLeft: GridBoxCornerI
  topRight: GridBoxCornerI
  bottomRight: GridBoxCornerI
  bottomLeft: GridBoxCornerI
}

// Scale a grid box from the configured resolution into the clip's pixel space,
// each axis independently — mirroring the evaluator's per-axis scaling.
const scaleBox = (
  box: GridBoxI | null | undefined,
  scaleX: number,
  scaleY: number
): GridBoxI | null => {
  if (!box) return null
  const scaleCorner = (corner: GridBoxCornerI): GridBoxCornerI => ({
    x: Math.round(corner.x * scaleX),
    y: Math.round(corner.y * scaleY),
  })
  return {
    topLeft: scaleCorner(box.topLeft),
    topRight: scaleCorner(box.topRight),
    bottomRight: scaleCorner(box.bottomRight),
    bottomLeft: scaleCorner(box.bottomLeft),
  }
}

const getBoxPoints = (box: GridBoxI | null): string => {
  if (!box) return ''
  const { topLeft, topRight, bottomRight, bottomLeft } = box
  return [
    `${topLeft.x},${topLeft.y}`,
    `${topRight.x},${topRight.y}`,
    `${bottomRight.x},${bottomRight.y}`,
    `${bottomLeft.x},${bottomLeft.y}`,
  ].join(' ')
}

const getCentroid = (box: GridBoxI | null): { x: number; y: number } => {
  if (!box) return { x: 0, y: 0 }
  const { topLeft, topRight, bottomRight, bottomLeft } = box
  return {
    x: (topLeft.x + topRight.x + bottomRight.x + bottomLeft.x) / 4,
    y: (topLeft.y + topRight.y + bottomRight.y + bottomLeft.y) / 4,
  }
}

/**
 * Renders a tailgate grid overlay on top of a video container.
 *
 * The SVG viewBox is the clip's decoded frame (`viewWidth` x `viewHeight`);
 * `preserveAspectRatio="xMidYMid meet"` letterboxes it to fit, matching how a
 * <video> letterboxes its own frame (object-fit: contain is the browser default
 * for video). The grid polygons are authored in the camera's configured
 * resolution, so they are scaled per-axis from that into the clip's pixel space
 * — the same transform the evaluator applies before computing its verdict, so
 * the operator sees exactly the (possibly stretched) grid the verdict used.
 *
 * Place as an absolutely-positioned sibling to VideoPlayer inside a
 * `position="relative"` wrapper Box.
 */
export const ScalableGridOverlay = ({
  tailgateGrid,
  viewWidth,
  viewHeight,
  cameraDimensions,
  clipDimensions,
}: ScalableGridOverlayIProps) => {
  const { t } = useTranslation()

  if (!tailgateGrid) return null

  const width = viewWidth ?? 0
  const height = viewHeight ?? 0
  // No decoded frame and no configured resolution — nothing to map against, so
  // decline to draw (the evaluator's `scalingMode: NONE` case).
  if (width <= 0 || height <= 0) return null

  const scaleX =
    cameraDimensions && clipDimensions
      ? clipDimensions.width / cameraDimensions.width
      : 1
  const scaleY =
    cameraDimensions && clipDimensions
      ? clipDimensions.height / cameraDimensions.height
      : 1

  const inBox = scaleBox(tailgateGrid.inBox, scaleX, scaleY)
  const outBox = scaleBox(tailgateGrid.outBox, scaleX, scaleY)

  const inCentroid = getCentroid(inBox)
  const outCentroid = getCentroid(outBox)

  return (
    <Box
      h='100%'
      left={0}
      pointerEvents='none'
      position='absolute'
      top={0}
      w='100%'
      zIndex={1}
    >
      <svg
        height='100%'
        preserveAspectRatio='xMidYMid meet'
        style={{ position: 'absolute', top: 0, left: 0 }}
        viewBox={`0 0 ${width} ${height}`}
        width='100%'
      >
        {inBox && (
          <>
            <polygon
              fill='rgba(72, 187, 120, 0.25)'
              points={getBoxPoints(inBox)}
              stroke='rgba(72, 187, 120, 0.8)'
              strokeWidth='4'
              style={{ vectorEffect: 'non-scaling-stroke' }}
            />
            <text
              fill='rgba(72, 187, 120, 0.9)'
              fontSize={Math.round(height * 0.04)}
              fontWeight='bold'
              textAnchor='middle'
              x={inCentroid.x}
              y={inCentroid.y}
            >
              {t('tailgate:grid_test_v2.zone_label_unsecured')}
            </text>
          </>
        )}
        {outBox && (
          <>
            <polygon
              fill='rgba(245, 101, 101, 0.25)'
              points={getBoxPoints(outBox)}
              stroke='rgba(245, 101, 101, 0.8)'
              strokeWidth='4'
              style={{ vectorEffect: 'non-scaling-stroke' }}
            />
            <text
              fill='rgba(245, 101, 101, 0.9)'
              fontSize={Math.round(height * 0.04)}
              fontWeight='bold'
              textAnchor='middle'
              x={outCentroid.x}
              y={outCentroid.y}
            >
              {t('tailgate:grid_test_v2.zone_label_secured')}
            </text>
          </>
        )}
      </svg>
    </Box>
  )
}
